import fileDownload from 'js-file-download';
import {useCallback} from 'react';
import {CSV_MIME_TYPE, scoutCsvFileName} from 'src/app/assistant/assistantCsv';
import {useExportAssistantQueryCsvMutation} from 'src/redux/queries/assistant';
import {useAlert} from 'src/shared/hooks/useAlert';
import {formatCount} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

const ROW_LIMIT_HEADER = 'x-row-limit-reached';

const rowLimitFrom = (
  headers: {[name: string]: unknown} | undefined
): number | null => {
  const key = Object.keys(headers ?? {}).find(
    name => name.toLowerCase() === ROW_LIMIT_HEADER
  );
  const limit = key ? Number(headers?.[key]) : NaN;
  return Number.isFinite(limit) && limit > 0 ? limit : null;
};

export interface AssistantQueryCsvDownload {
  download: () => Promise<void>;
  downloading: boolean;
}

/**
 * Saves one Scout query's server re-run as `scout-query-<local timestamp>.csv`, noting when the
 * server capped the rows; failures show the server's message.
 * @registry Downloads a saved Scout reply's query result as CSV (server re-run under current access).
 */
export const useAssistantQueryCsvDownload = (
  conversationId: number,
  toolUseId: string
): AssistantQueryCsvDownload => {
  const {setAlert} = useAlert();
  const [exportCsv, {isLoading}] = useExportAssistantQueryCsvMutation();

  const download = useCallback(async () => {
    try {
      const file = await exportCsv({conversationId, toolUseId}).unwrap();
      if (!file) {
        setAlert('Failed to download the query result.', 'error');
        return;
      }
      fileDownload(
        file.data,
        scoutCsvFileName('query', new Date()),
        CSV_MIME_TYPE
      );
      const rowLimit = rowLimitFrom(file.headers);
      if (rowLimit !== null) {
        setAlert(
          `The file contains the first ${formatCount(rowLimit)} rows.`,
          'info'
        );
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [conversationId, exportCsv, setAlert, toolUseId]);

  return {download, downloading: isLoading};
};
